import { Accessor, createEffect, Setter } from 'solid-js';

export enum MessageDataType {
  Data = 'data',
  RequestData = 'request_data',
}

export type Message<T> =
  | {
      type: MessageDataType.Data;
      payload: T;
    }
  | {
      type: MessageDataType.RequestData;
    };

/**
 * Simple synchronization utility to pass data between windows.
 * Uses BroadCastChannel to pass data.
 * @param channelName Use name between different syncer instances that you want to sync
 * @param isParent Parent provides data and when non-parent is created
 * it requests latest data from parent.
 * @param syncObject Signal with data which will be tracked and synced with other
 * syncer instances with same channel name.
 */
export function createBCSyncer<T>(
  channelName: string,
  isParent: true,
  syncObject: Accessor<T>
): void;
// eslint-disable-next-line no-redeclare
export function createBCSyncer<T>(
  channelName: string,
  isParent: false,
  syncObject: Setter<T>
): void;
// eslint-disable-next-line no-redeclare
export function createBCSyncer<T>(
  channelName: string,
  isParent: boolean,
  syncObject: Accessor<T> | Setter<T>
): void {
  const channel = new BroadcastChannel(channelName);

  function postMessage<T>(message: Message<T>): void {
    channel.postMessage(JSON.stringify(message));
  }

  if (isParent) {
    // Payload is saved here so that latest value can be requested by subscriber at any time.
    let latestPayload: T | null = null;

    createEffect(() => {
      const newValue = syncObject();
      latestPayload = newValue;
      postMessage({ type: MessageDataType.Data, payload: newValue });
    });

    channel.addEventListener('message', (ev: MessageEvent<string>) => {
      const response = JSON.parse(ev.data) as Message<T>;
      if (response.type === MessageDataType.RequestData) {
        // TODO: Probably only one who requested should read the data.
        // This probably requires one more parameter for 'request_data' - id
        // and probably new Message type.
        postMessage({ type: MessageDataType.Data, payload: latestPayload });
        return;
      }
    });
  } else {
    channel.addEventListener('message', (ev: MessageEvent<string>) => {
      const response = JSON.parse(ev.data) as Message<T>;
      if (response.type === MessageDataType.RequestData) {
        return;
      }
      syncObject(() => response.payload);
    });
    // Once subscription is initialized we should request for up-to-date data.
    postMessage({ type: MessageDataType.RequestData });
  }
}
